import {
  Alert,
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCalendar,
  IconExternalLink,
  IconFlag,
  IconGift,
  IconUser,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { useWish } from '../api/queries';
import { formatFullDateTime, formatMoney } from '../lib/format';

interface WishDetailsProps {
  wishId: string | null;
  onClose: () => void;
}

/** Mobile-first details: the product link is deliberately separate from the list card. */
export function WishDetails({ wishId, onClose }: WishDetailsProps) {
  const { data, isPending, error } = useWish(wishId);

  return (
    <Drawer
      opened={wishId !== null}
      onClose={onClose}
      position="right"
      size="100%"
      padding="lg"
      title={<Text fw={600}>Хотелка</Text>}
      styles={{ content: { maxWidth: '30rem' } }}
    >
      {error ? (
        <Alert color="red" variant="light" title="Хотелка недоступна">
          {error.message}
        </Alert>
      ) : isPending || !data ? (
        <Stack gap="md">
          <Skeleton height={72} radius="sm" />
          <Skeleton height={20} radius="sm" />
          <Skeleton height={84} radius="sm" />
        </Stack>
      ) : (
        <Stack gap="lg">
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon size="lg" radius="md" variant="light" style={{ flexShrink: 0 }}>
              <IconGift size={20} stroke={1.5} />
            </ThemeIcon>
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Text fz="lg" fw={600} style={{ overflowWrap: 'anywhere' }}>
                {data.title}
              </Text>
              <Text
                fz={28}
                fw={600}
                style={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
              >
                {formatMoney(data.price)}
              </Text>
            </Stack>
          </Group>

          <Divider />

          <Stack gap="md">
            <Field label="Кто хочет" icon={<IconUser size={15} stroke={1.5} />}>
              {data.initiator}
            </Field>

            <Field label="Приоритет" icon={<IconFlag size={15} stroke={1.5} />}>
              <Badge color={data.priority === 0 ? 'gray' : 'brand'} variant="light">
                {data.priority === 0 ? 'Не задан' : data.priority}
              </Badge>
            </Field>

            {data.description && (
              <Field label="Описание">
                <Text
                  size="sm"
                  style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {data.description}
                </Text>
              </Field>
            )}

            <Field label="Добавлено" icon={<IconCalendar size={15} stroke={1.5} />}>
              {formatFullDateTime(data.created_at)}
            </Field>
          </Stack>

          {data.url && (
            <Button
              component="a"
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconExternalLink size={18} stroke={1.5} />}
              fullWidth
            >
              Открыть товар
            </Button>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap={4}>
      <Group gap={5} c="dimmed">
        {icon}
        <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: '0.06em' }}>
          {label}
        </Text>
      </Group>
      {typeof children === 'string' ? (
        <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Stack>
  );
}
